import React, { useState, useEffect, useRef } from 'react';

const MathArtGeneratorGame = ({ gameData, onComplete }) => {
    const [currentProblemIndex, setCurrentProblemIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [userAnswer, setUserAnswer] = useState('');
    const [feedback, setFeedback] = useState(null); 
    const [solvedProblems, setSolvedProblems] = useState([]);
    const [isFinished, setIsFinished] = useState(false);
    const canvasRef = useRef(null);

    const totalProblems = gameData?.problems?.length || 0;
    const currentProblem = gameData?.problems?.[currentProblemIndex];

    useEffect(() => {
        if (canvasRef.current) drawArt();
    }, [solvedProblems, isFinished]);

    const drawArt = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const width = canvas.width;
        const height = canvas.height;
        const centerX = width / 2;
        const centerY = height / 2;

        // Background with premium digital depth
        ctx.fillStyle = '#0f172a'; // Deep slate
        ctx.fillRect(0, 0, width, height);
        
        // Subtle grid pattern
        ctx.strokeStyle = 'rgba(255,255,255,0.03)';
        ctx.lineWidth = 1;
        for(let i = 0; i < width; i += 50) {
            ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, height); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(width, i); ctx.stroke();
        }

        // Draw each solved problem as premium digital element
        solvedProblems.forEach((problem, index) => {
            const angle = (index / totalProblems) * 2 * Math.PI;
            const radius = 60 + (index * 35);
            const x = centerX + Math.cos(angle) * radius;
            const y = centerY + Math.sin(angle) * radius;
            const size = 60 + (index * 15);

            ctx.fillStyle = problem.color || '#ec4899';
            ctx.strokeStyle = 'rgba(255,255,255,0.2)';
            ctx.lineWidth = 4;
            ctx.shadowBlur = 40;
            ctx.shadowColor = (problem.color || '#ec4899') + '99';
            ctx.globalAlpha = 0.9;

            switch (problem.shape) {
                case 'circle':
                    ctx.beginPath(); ctx.arc(x, y, size / 2, 0, 2 * Math.PI); ctx.fill(); ctx.stroke(); break;
                case 'square':
                    ctx.fillRect(x - size / 2, y - size / 2, size, size); ctx.strokeRect(x - size / 2, y - size / 2, size, size); break;
                case 'triangle':
                    ctx.beginPath(); ctx.moveTo(x, y - size / 2); ctx.lineTo(x - size / 2, y + size / 2); ctx.lineTo(x + size / 2, y + size / 2); ctx.closePath(); ctx.fill(); ctx.stroke(); break;
                case 'star':
                    drawStar(ctx, x, y, 5, size / 2, size / 4); ctx.stroke(); break;
                case 'hexagon':
                    drawHexagon(ctx, x, y, size / 2); ctx.stroke(); break;
                default:
                    ctx.beginPath(); ctx.arc(x, y, size / 2, 0, 2 * Math.PI); ctx.fill(); ctx.stroke();
            }
            ctx.globalAlpha = 1.0;
            ctx.shadowBlur = 0;
        });
    };

    const drawStar = (ctx, cx, cy, spikes, outerRadius, innerRadius) => {
        let rot = Math.PI / 2 * 3;
        const step = Math.PI / spikes;
        ctx.beginPath(); ctx.moveTo(cx, cy - outerRadius);
        for (let i = 0; i < spikes; i++) {
            ctx.lineTo(cx + Math.cos(rot) * outerRadius, cy + Math.sin(rot) * outerRadius); rot += step;
            ctx.lineTo(cx + Math.cos(rot) * innerRadius, cy + Math.sin(rot) * innerRadius); rot += step;
        }
        ctx.lineTo(cx, cy - outerRadius); ctx.closePath(); ctx.fill();
    };

    const drawHexagon = (ctx, x, y, radius) => {
        ctx.beginPath();
        for (let i = 0; i < 6; i++) {
            const angle = (Math.PI / 3) * i;
            const hx = x + radius * Math.cos(angle);
            const hy = y + radius * Math.sin(angle);
            if (i === 0) ctx.moveTo(hx, hy); else ctx.lineTo(hx, hy);
        }
        ctx.closePath(); ctx.fill();
    };

    const handleCheck = () => {
        if (!userAnswer || feedback) return;
        const numAnswer = parseInt(userAnswer);

        if (numAnswer === currentProblem.answer) {
            setFeedback('correct');
            const pointsToAdd = gameData.points_per_problem || 20;
            const newScore = score + pointsToAdd;
            setScore(newScore);
            setSolvedProblems([...solvedProblems, currentProblem]);
            if (navigator.vibrate) navigator.vibrate([100, 50, 100]);

            setTimeout(() => {
                setFeedback(null);
                setUserAnswer('');
                if (currentProblemIndex < totalProblems - 1) {
                    setCurrentProblemIndex(prev => prev + 1);
                } else {
                    setIsFinished(true);
                    if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 300]);
                }
            }, 3000);
        } else {
            setFeedback('wrong');
            if (navigator.vibrate) navigator.vibrate([50, 50, 50]);
            setTimeout(() => setFeedback(null), 1500);
        }
    };

    if (!gameData || !gameData.problems) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-pink-100 animate-pulse bg-white">
                <div className="text-lg md:text-4xl md:text-[100px] mb-8">🎨</div>
                <h3 className="text-base md:text-3xl font-black text-pink-900 tracking-tighter uppercase">Ruošiama drobė...</h3>
            </div>
        );
    }

    const progress = (solvedProblems.length / totalProblems) * 100;

    return (
        <div className="space-y-3 md:space-y-8 animate-slide-up select-none">
            {/* Header / Progress Area */}
            <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-pink-50">
                <div className="absolute top-0 right-0 w-64 h-64 bg-pink-500/5 rounded-full blur-[80px] pointer-events-none"></div>
                <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-pink-100 rounded-2xl flex items-center justify-center text-base md:text-3xl shadow-inner animate-float">
                            🎨
                        </div>
                        <div>
                            <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                                Matematikos Menas
                            </h3>
                            <p className="text-slate-500 font-bold italic font-serif">
                                Išspręsk uždavinius ir generuok skaitmeninį šedevrą!
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="bg-white/50 px-6 py-3 rounded-2xl border border-white/50 shadow-inner">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Kūryba</p>
                            <p className="text-2xl font-black text-pink-600 leading-none">⭐ {score}</p>
                        </div>
                        <div className="bg-white/50 px-6 py-3 rounded-2xl border border-white/50 shadow-inner text-center min-w-[100px]">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Progresas</p>
                            <p className="text-2xl font-black text-slate-800 leading-none">{solvedProblems.length}/{totalProblems}</p>
                        </div>
                    </div>
                </div>

                <div className="mt-6 w-full bg-slate-200/50 rounded-full h-3 p-1 shadow-inner overflow-hidden border border-slate-300">
                    <div 
                        className="h-full bg-gradient-to-r from-pink-400 via-rose-500 to-orange-600 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(236,72,153,0.3)]"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 md:p-8 items-stretch">
                
                {/* Generative Canvas Display (Left) */}
                <div className="lg:col-span-7">
                    <div className="premium-glass rounded-2xl md:rounded-[4rem] p-3 md:p-8 lg:p-4 md:p-12 shadow-2xl border-white/60 bg-slate-900 relative overflow-hidden flex flex-col items-center h-full justify-center">
                        <div className="w-full flex items-center justify-between mb-8 px-4">
                            <h4 className="text-[10px] font-black text-pink-400 uppercase tracking-[0.5em]">Skaitmeninė Drobė</h4>
                            {isFinished && (
                                <div className="bg-emerald-500 text-white px-6 py-2 rounded-full text-[10px] font-black animate-pulse shadow-xl">
                                    GALERIJA BAIGTA
                                </div>
                            )}
                        </div>
                        
                        <div className="relative w-full aspect-square max-w-[600px] group scale-95 lg:scale-100 transition-transform duration-700">
                            <div className="absolute -inset-10 bg-gradient-to-br from-pink-500/20 to-orange-500/20 rounded-full blur-[100px] group-hover:opacity-100 opacity-60 transition-opacity" />
                            <canvas
                                ref={canvasRef}
                                width={800}
                                height={800}
                                className="w-full h-full border-[12px] border-slate-800 rounded-2xl md:rounded-[4rem] shadow-[0_40px_80px_rgba(0,0,0,0.6)] bg-slate-900 relative z-10 overflow-hidden transition-all duration-500 group-hover:scale-[1.02]"
                            />
                            {solvedProblems.length === 0 && (
                                <div className="absolute inset-0 flex flex-col items-center justify-center p-4 md:p-12 text-center z-20 space-y-6">
                                    <div className="text-8xl opacity-20">🎨</div>
                                    <p className="text-2xl font-black text-slate-500 uppercase tracking-tighter italic font-serif">
                                        Išspręsk uždavinį, kad įžiebtum pirmąją spalvą!
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Computational Studio Interaction (Right) */}
                <div className="lg:col-span-5 flex flex-col justify-center gap-2 md:p-6">
                    {!isFinished ? (
                        <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 lg:p-4 md:p-14 shadow-2xl border-white/40 bg-white/40 backdrop-blur-xl animate-pop-in relative overflow-hidden flex flex-col justify-center min-h-[50vh]">
                            <div className="absolute top-0 right-0 p-3 md:p-8 opacity-[0.03] text-2xl md:text-6xl md:text-[200px] font-black select-none pointer-events-none">?</div>
                            <div className="bg-pink-100 text-pink-600 px-6 py-2 rounded-2xl text-[10px] font-black uppercase tracking-widest mb-10 inline-block w-fit shadow-inner">
                                Algoritmas {currentProblemIndex + 1}
                            </div>
                            
                            <div className="space-y-4 md:space-y-12 relative z-10">
                                <h4 className="text-lg md:text-4xl md:text-xl md:text-5xl font-black text-slate-800 leading-tight tracking-tighter uppercase break-words">
                                    {currentProblem?.question}
                                </h4>

                                <div className="space-y-3 md:space-y-8">
                                    <div className="relative group">
                                        <input
                                            type="number"
                                            value={userAnswer}
                                            onChange={(e) => setUserAnswer(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && handleCheck()}
                                            placeholder="?"
                                            className="w-full p-4 md:p-12 text-8xl text-center border-[6px] border-slate-100 rounded-[3.5rem] focus:border-pink-500 focus:outline-none transition-all font-black bg-white shadow-2xl placeholder:text-slate-100 text-slate-800"
                                        />
                                        <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-2 rounded-full text-[10px] font-black uppercase tracking-widest shadow-xl">
                                            Atsakymas
                                        </div>
                                    </div>

                                    <button
                                        onClick={handleCheck}
                                        disabled={!userAnswer || !!feedback}
                                        className={`
                                            premium-button-3d w-full py-4 md:py-10 bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-xl md:rounded-[3rem] font-black text-lg md:text-4xl shadow-2xl shadow-pink-900/20 transition-all border-b-[12px] border-pink-800 uppercase tracking-[0.2em]
                                            ${userAnswer && !feedback ? 'scale-105 active:translate-y-2 active:border-b-0' : 'opacity-40'}
                                        `}
                                    >
                                        GENERUOTI 🚀
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-16 text-center shadow-2xl border-white/60 bg-gradient-to-br from-slate-900 to-slate-800 text-white animate-pop-in relative overflow-hidden flex flex-col items-center">
                             <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(236,72,153,0.2)_0%,transparent_70%)] pointer-events-none" />
                             <div className="text-[160px] mb-8 drop-shadow-[0_0_60px_rgba(255,255,255,0.3)] animate-float">🏆</div>
                             <h3 className="text-2xl md:text-6xl font-black mb-6 uppercase tracking-tighter leading-none">Šedevras Baigtas!</h3>
                             <p className="text-2xl font-bold mb-12 opacity-80 italic font-serif leading-relaxed px-6">
                                Jūsų matematinių skaičiavimų seka sukūrė nuostabų generatyvų kūrinį!
                             </p>
                             <div className="bg-white/10 rounded-xl md:rounded-[3rem] p-4 md:p-10 mb-12 border-4 border-white/20 backdrop-blur-xl w-full max-w-sm shadow-[0_20px_50px_rgba(0,0,0,0.3)]">
                                <p className="text-[10px] font-black uppercase tracking-[0.4em] mb-4 text-pink-400">Gauti Taškai</p>
                                <p className="text-8xl font-black italic text-white drop-shadow-[0_0_20px_rgba(255,255,255,0.4)]">{score}</p>
                             </div>
                             <button
                                onClick={() => onComplete?.(score)}
                                className="w-full py-4 md:py-10 bg-white text-slate-900 rounded-[3.5rem] font-black text-lg md:text-4xl shadow-[0_20px_60px_rgba(255,255,255,0.3)] hover:scale-105 active:scale-95 transition-all uppercase tracking-widest"
                             >
                                IŠSAUGOTI ✨
                             </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Global Computational Feedback Overlay */}
            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[1000] p-2 md:p-6">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-2xl w-full p-4 md:p-16 rounded-2xl md:rounded-[4rem] bg-white shadow-[0_40px_100px_rgba(0,0,0,0.5)] relative animate-pop-in border-[12px]
                        ${feedback === 'correct' ? 'border-emerald-500' : 'border-pink-500'}
                    `}>
                        <div className="absolute -top-4 md:p-14 left-1/2 -translate-x-1/2 w-28 h-28 bg-white rounded-full flex items-center justify-center text-2xl md:text-6xl shadow-2xl border-8 border-inherit animate-bounce">
                            {feedback === 'correct' ? '🎨' : '🧐'}
                        </div>
                        
                        <div className="text-center space-y-3 md:space-y-8">
                            <h2 className={`text-xl md:text-5xl font-black uppercase tracking-tighter ${feedback === 'correct' ? 'text-emerald-600' : 'text-pink-600'}`}>
                                {feedback === 'correct' ? 'TEISINGAS SKAIČIAVIMAS!' : 'SISTEMOS KLAIDA!'}
                            </h2>

                            <div className={`p-4 md:p-10 rounded-xl md:rounded-[3rem] border-2 text-left italic font-serif leading-relaxed ${feedback === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-pink-50 border-pink-100'}`}>
                                <p className="text-2xl font-bold text-slate-700 text-center leading-relaxed italic">
                                    {feedback === 'correct' ? 'Puiku! Tavo atsakymas yra tikslus. Algoritmas sėkmingai sugeneravo naują elementą į drobę!' : 'Oi! Panašu, kad skaičiavimuose įsivėlė klaida. Peržiūrėk uždavinį dar kartą, menininke!'}
                                </p>
                            </div>

                            <div className="flex items-center justify-center gap-4">
                                <div className="flex gap-2">
                                    <div className="w-3 h-3 bg-slate-200 rounded-full animate-bounce" />
                                    <div className="w-3 h-3 bg-slate-200 rounded-full animate-bounce [animation-delay:0.2s]" />
                                    <div className="w-3 h-3 bg-slate-200 rounded-full animate-bounce [animation-delay:0.4s]" />
                                </div>
                                <span className="text-sm font-black text-slate-300 uppercase tracking-[0.3em] ml-2">
                                    {feedback === 'correct' ? 'Generuojame spalvas...' : 'Tęsiame tyrimą...'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MathArtGeneratorGame;
